import { DirectionProvider } from '@radix-ui/react-direction';
import { Loader } from '@fe-learner-common/components/Loader';
import { useElementAttribute } from '@fe-learner-common/hooks/settings/useElementAttributes';
import { setupLuxon } from '@fe-learner-common/utils/date';
import { DetailsPageSkeleton } from '@components/DetailsPageSkeleton';
import { TW_ROOT } from '@utils/constants';
import { useApplySelfregPageConfig } from '@routes/selfreg/hooks/useApplySelfregPageConfig';
import {
  BaseHTTPError,
  ForbiddenError,
  NotFoundError,
  useAppContext,
  useBidiSupport,
  useLanguages,
} from '@fe-core';
import { useParams } from 'react-router-dom';
import { useError } from '@hooks/useError';
import { SelfRegRoute } from '@routes/selfreg/SelfregRoute';
import { useSelfregSessionDetails } from './hooks/data/useSelfregSessionDetails';
import { publishBaseErrorEvent } from '@fe-core-events';
import { IntlProvider, useIntl } from 'react-intl';

const TARGET_ELEMENT = `#${TW_ROOT}`;

const useHandleError = (error: BaseHTTPError | null) => {
  const emitError = useError();
  const intl = useIntl();

  if (!error) {
    return;
  }

  switch (true) {
    case error instanceof ForbiddenError:
      publishBaseErrorEvent({
        consumer: 'fe-learner-catalog',
        event: 'feedback.ui.error.base',
        type: 'modal',
        status: 'error',
        config: {
          title: intl.formatMessage({
            id: 'catalog.course.error.selfreg.disabled.title',
          }),
          description: intl.formatMessage({
            id: 'catalog.course.error.selfreg.disabled.description',
          }),
        },
      });
      break;
    case error instanceof NotFoundError:
      publishBaseErrorEvent({
        consumer: 'fe-learner-catalog',
        event: 'feedback.ui.error.base',
        type: 'modal',
        status: 'error',
        config: {
          title: intl.formatMessage({
            id: 'learner.common.error.notFound.title',
          }),
          description: intl.formatMessage({
            id: 'learner.common.error.notFound.description',
          }),
        },
      });
      break;

    default:
      emitError({
        error: error,
        errorMapOverride: {
          type: 'modal',
        },
      });
  }
};

export const SelfRegRouteLoader = () => {
  useApplySelfregPageConfig();
  const intl = useIntl();
  const { sessionId } = useParams();
  const { isLoading: isLoadingAppContext } = useAppContext();

  const {
    data,
    error,
    isLoading: isLoadingSessionDetails,
  } = useSelfregSessionDetails({
    enabled: true,
    sessionId,
  });

  const courseLocale = data?.locale;

  const {
    isLoading: isLoadingLanguage,
    isError: isLanguageError,
    keys,
  } = useLanguages({
    locale: courseLocale,
    enabled: !!courseLocale,
    bundles: ['fe-learner'],
  });

  // no course locale, or its language failed to load: use the user's
  const hasCourseLocale = !!courseLocale && !isLanguageError;
  const locale = hasCourseLocale ? courseLocale : intl.locale;
  const messages = hasCourseLocale ? keys : intl.messages;

  setupLuxon(locale);
  useElementAttribute(
    {
      lang: locale,
    },
    TARGET_ELEMENT,
  );

  const { dir } = useBidiSupport({
    locale,
    elementToUpdate: TARGET_ELEMENT,
  });

  useHandleError(error);

  const isLoading =
    isLoadingAppContext || isLoadingSessionDetails || isLoadingLanguage;

  return (
    <Loader isLoading={isLoading} skeleton={<DetailsPageSkeleton />}>
      <IntlProvider
        fallbackOnEmptyString={true}
        locale={locale}
        messages={messages}
      >
        <DirectionProvider dir={dir}>
          <SelfRegRoute />
        </DirectionProvider>
      </IntlProvider>
    </Loader>
  );
};
